import { sortByAccessor } from "../../../utils/sortRows";
import { addDays, dayName, formatDay, isWeekend, weekDates } from "../../../utils/weekDates";

export const STATUS = {
  submitted: { label: "Submitted", tone: "amber" },
  sent_back: { label: "Sent back", tone: "red" },
  not_submitted: { label: "Not submitted", tone: "grey" },
  approved: { label: "Approved", tone: "green" },
};
export const STATUS_ORDER = Object.keys(STATUS);

export const TYPE_LABEL = { permanent: "Permanent", contractor: "Contractors" };
export const typeOf = (person) => (person.kind === "ghost" ? "contractor" : "permanent");

export const weekLabel = (weekStart) => `${formatDay(weekStart)} – ${formatDay(addDays(weekStart, 6), true)}`;

// 6 -> "6h", 11.5 -> "11.5h", 0 -> "0h"
export const fmtHours = (n) => `${Math.round((Number(n) || 0) * 100) / 100}h`;
export const sumDays = (days) => Object.values(days || {}).reduce((s, h) => s + h, 0);

const list = (names) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]);

// Working days up to today with less logged than capacity (less leave), as "Monday and Tuesday"; null when none.
export const shortfall = ({ weekStart, today, dailyCapacity, leave, lines }) => {
  const short = weekDates(weekStart).filter((d) => {
    if (isWeekend(d) || d > today) return false;
    const due = Math.max(0, dailyCapacity - (leave?.[d] || 0));
    return lines.reduce((s, l) => s + (l.days[d] || 0), 0) < due;
  });
  return short.length ? list(short.map((d) => dayName(d, "long"))) : null;
};

// ---- Team table ----
export const TEAM_COLUMNS = [
  { key: "person", label: "Person", visible: true },
  { key: "status", label: "Status", visible: true },
  { key: "logged", label: "Logged", visible: true },
  { key: "scheduled", label: "Scheduled", visible: true },
  { key: "billable", label: "Billable", visible: true },
  { key: "missing", label: "Missing", visible: true },
  { key: "type", label: "Type", visible: false },
  { key: "job_title", label: "Job title", visible: false },
  { key: "leave", label: "Leave", visible: false },
];

export const STORAGE_KEY = "timesheetTeamColumns";

export const GROUPS = {
  status: { label: "Status", get: (p) => STATUS[p.status].label },
  type: { label: "Type", get: (p) => TYPE_LABEL[typeOf(p)] },
};
export const GROUP_ORDER = { status: STATUS_ORDER.map((s) => STATUS[s].label), type: Object.values(TYPE_LABEL) };

export const DEFAULT_FILTERS = { search: "", type: "", status: "", project: "", groupBy: "status", sort: null };

const matches = (q, values) => !q || values.some((v) => (v || "").toString().toLowerCase().includes(q));

export const filterPeople = (rows, f) => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((p) =>
    (!f.type || typeOf(p) === f.type)
    && (!f.status || p.status === f.status)
    && (!f.project || p.project_ids.includes(Number(f.project)))
    && matches(q, [p.name, p.job_title])
  );
};

const ACCESSORS = {
  person: (p) => p.name,
  status: (p) => STATUS_ORDER.indexOf(p.status),
  logged: (p) => p.logged_hours,
  scheduled: (p) => p.scheduled_hours,
  billable: (p) => p.billable_hours,
  missing: (p) => p.missing_hours,
  type: (p) => TYPE_LABEL[typeOf(p)],
  job_title: (p) => p.job_title,
  leave: (p) => p.weekly_hours - p.capacity_hours,
};

export const sortPeople = (rows, sort) => sortByAccessor(rows, sort, sort && ACCESSORS[sort.key]);

// Plain values of each column for the summary sheet and header sorts.
const EXPORT_VALUES = {
  ...ACCESSORS,
  status: (p) => STATUS[p.status].label,
  logged: (p) => `${p.logged_hours} / ${p.capacity_hours}`,
};
export const exportColumns = (columns) => columns.map((c) => ({ key: c.key, label: c.label, visible: c.visible, value: EXPORT_VALUES[c.key] }));

// One row per line per day: what "Export to Excel" writes to the line-by-line sheet.
export const ENTRY_COLUMNS = [
  { label: "Date", value: (e) => e.work_date },
  { label: "Person", value: (e) => e.employee_name },
  { label: "Client", value: (e) => e.company_name },
  { label: "Project", value: (e) => e.project_name || "Internal" },
  { label: "Activity", value: (e) => e.activity_type_name },
  { label: "Milestone", value: (e) => e.milestone_name },
  { label: "Hours", value: (e) => Number(e.hours_worked) },
  { label: "Billable", value: (e) => (e.is_billable ? "Yes" : "No") },
  { label: "Description", value: (e) => e.description },
];
